import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from './use-auth';

// Feature area mapping based on URL paths
const getFeatureArea = (path: string): string => {
  if (path.startsWith('/dashboard') || path === '/') return 'dashboard';
  if (path.startsWith('/analytics')) return 'analytics';
  // These three feed Customer Health adoption, so the returned value has to
  // match the module key in franchises.settings.features.modules, not the URL.
  if (path.startsWith('/analysis')) return 'analysis';
  if (path.startsWith('/site-intelligence')) return 'sites';
  if (path.startsWith('/compliance')) return 'quality_assurance';
  if (path.startsWith('/communications')) return 'communications';
  if (path.startsWith('/documents')) return 'documents';
  if (path.startsWith('/royalties')) return 'royalties';
  if (path.startsWith('/location-command')) return 'location_command';
  if (path.startsWith('/location-diagnostics')) return 'location_diagnostics';
  if (path.startsWith('/reviews')) return 'reviews';
  if (path.startsWith('/support')) return 'support';
  if (path.startsWith('/resources')) return 'resources';
  if (path.startsWith('/task-management')) return 'task_management';
  if (path.startsWith('/franchise-api')) return 'franchise_api';
  if (path.startsWith('/franchisee-control')) return 'franchisee_control';
  if (path.startsWith('/franchisor-dashboard')) return 'franchisor_dashboard';
  if (path.startsWith('/developer')) return 'developer';
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/account-manager')) return 'account_manager';
  if (path.startsWith('/account')) return 'account';
  // The merged page. Kept above the three retired paths below it, which now redirect here —
  // without this, every visit would land in whichever legacy key the redirect passed through.
  if (path.startsWith('/data-apis')) return 'data_apis';
  if (path.startsWith('/api-management')) return 'api_management';
  if (path.startsWith('/ssot')) return 'ssot_viewer';
  if (path.startsWith('/brands')) return 'brands';
  if (path.startsWith('/brand-select')) return 'brand_select';
  if (path.startsWith('/parent-dashboard')) return 'parent_dashboard';
  if (path.startsWith('/learning')) return 'learning';
  if (path.startsWith('/demo-account-creator')) return 'demo_account_creator';
  if (path.startsWith('/demo')) return 'demo';
  if (path.startsWith('/user-health')) return 'user_health';
  if (path.startsWith('/marketing')) return 'marketing';
  return 'other';
};

// Generate a human-readable page title from the path
const getPageTitle = (path: string): string => {
  // Remove leading slash and split by slashes
  const parts = path.replace(/^\//, '').split('/');
  
  // Handle empty path (root)
  if (parts[0] === '' || path === '/') return 'Dashboard';
  
  // Convert kebab-case to Title Case
  return parts
    .map(part => 
      part
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    )
    .join(' > ');
};

interface PageTrackingOptions {
  enabled?: boolean;
}

/**
 * Hook to track page views for customer health analytics
 * 
 * Usage:
 * ```tsx
 * function App() {
 *   usePageTracking(); // Add to root component
 *   return <Router>...</Router>;
 * }
 * ```
 */
export function usePageTracking(options: PageTrackingOptions = {}) {
  const { enabled = true } = options;
  const [location] = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  const previousPath = useRef<string | null>(null);
  const pageStartTime = useRef<number>(Date.now());
  
  // Track page view
  const trackPageView = useCallback(async (
    pagePath: string, 
    referrerPath?: string,
    timeOnPreviousPageSeconds?: number
  ) => {
    // Don't track if user is not authenticated or tracking is disabled
    if (!isAuthenticated || !user || !enabled) return;
    
    // Don't track certain paths
    const ignoredPaths = ['/login', '/register', '/auth', '/api'];
    if (ignoredPaths.some(p => pagePath.startsWith(p))) return;
    
    try {
      await fetch('/api/user/page-view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          pagePath,
          pageTitle: getPageTitle(pagePath),
          featureArea: getFeatureArea(pagePath),
          timeOnPageSeconds: timeOnPreviousPageSeconds || 0,
          referrerPath,
        }),
      });
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('Page tracking failed:', error);
    }
  }, [isAuthenticated, user, enabled]);
  
  // Track when location changes
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    
    // Calculate time spent on previous page
    const now = Date.now();
    const timeOnPreviousPageSeconds = previousPath.current 
      ? Math.round((now - pageStartTime.current) / 1000)
      : 0;
    
    // Track the new page view
    trackPageView(location, previousPath.current || undefined, timeOnPreviousPageSeconds);
    
    // Update refs for next navigation
    previousPath.current = location;
    pageStartTime.current = now;
  }, [location, enabled, isAuthenticated, trackPageView]);
  
  // Track time on page when user leaves
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    
    const handleBeforeUnload = () => {
      const timeOnPageSeconds = Math.round((Date.now() - pageStartTime.current) / 1000);
      
      // Use sendBeacon for reliable tracking on page unload
      if (navigator.sendBeacon && previousPath.current) {
        navigator.sendBeacon('/api/user/page-view', JSON.stringify({
          pagePath: previousPath.current,
          pageTitle: getPageTitle(previousPath.current),
          featureArea: getFeatureArea(previousPath.current),
          timeOnPageSeconds,
        }));
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, isAuthenticated]);
  
  return { trackPageView };
}

export default usePageTracking;

